import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Plus, Trash2, CalendarClock, CalendarDays } from 'lucide-react';
import {
  DEFAULT_PRIVATE_SESSION_OPTIONS,
  DEFAULT_AVAILABILITY_NOTE,
  isPrivateLesson,
} from '../utils/privateLesson';

/**
 * Class format picker + private-lesson settings.
 * `value` is the form state object, `onPatch(partial)` merges changes into it.
 */
export const PrivateLessonSettings = ({ value, onPatch, idPrefix = 'fmt' }) => {
  const isPrivate = isPrivateLesson(value);
  const options = Array.isArray(value.private_session_options) && value.private_session_options.length
    ? value.private_session_options
    : DEFAULT_PRIVATE_SESSION_OPTIONS;

  const setMode = (mode) => {
    if (mode === 'by_appointment') {
      onPatch({
        scheduling_mode: 'by_appointment',
        private_session_options: options,
        availability_note: value.availability_note || DEFAULT_AVAILABILITY_NOTE,
      });
    } else {
      onPatch({ scheduling_mode: 'term' });
    }
  };

  const updateOption = (idx, patch) => {
    const next = options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    onPatch({ private_session_options: next });
  };

  const addOption = () =>
    onPatch({ private_session_options: [...options, { label: '', price: 0 }] });

  const removeOption = (idx) =>
    onPatch({ private_session_options: options.filter((_, i) => i !== idx) });

  return (
    <div className="border border-stone-200 rounded-md p-4 bg-stone-50" data-testid="class-format-settings">
      <Label className="font-medium text-stone-900 block mb-3">Class Format</Label>
      <div className="space-y-3">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name={`${idPrefix}_scheduling_mode`}
            checked={!isPrivate}
            onChange={() => setMode('term')}
            className="mt-0.5 accent-cafa-red"
            data-testid="format-term-radio"
          />
          <span>
            <span className="flex items-center gap-1.5 font-medium text-stone-800 text-sm">
              <CalendarDays size={14} /> Term Class
            </span>
            <span className="block text-xs text-stone-500">Repeats on set days over a term, with start and end dates</span>
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="radio"
            name={`${idPrefix}_scheduling_mode`}
            checked={isPrivate}
            onChange={() => setMode('by_appointment')}
            className="mt-0.5 accent-cafa-red"
            data-testid="format-private-radio"
          />
          <span>
            <span className="flex items-center gap-1.5 font-medium text-stone-800 text-sm">
              <CalendarClock size={14} /> Private Lesson (by appointment)
            </span>
            <span className="block text-xs text-stone-500">
              One-on-one, one-off session. No day, date or time — the student pays for a session and you arrange the time together.
            </span>
          </span>
        </label>
      </div>

      {isPrivate && (
        <div className="mt-4 pt-4 border-t border-stone-200 space-y-4" data-testid="private-lesson-fields">
          <div>
            <Label>Session lengths &amp; prices</Label>
            <p className="text-xs text-stone-500 mt-0.5 mb-2">
              What a student can book and pay for online. The lowest price shows on the class card as &ldquo;from&rdquo;.
            </p>
            <div className="space-y-2">
              {options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`session-option-row-${idx}`}>
                  <Input
                    value={opt.label || ''}
                    onChange={(e) => updateOption(idx, { label: e.target.value })}
                    placeholder="e.g. 1 hour"
                    className="flex-1"
                    data-testid={`session-option-label-${idx}`}
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-stone-500">$</span>
                    <Input
                      type="number"
                      min="0"
                      value={opt.price ?? 0}
                      onChange={(e) => updateOption(idx, { price: parseFloat(e.target.value) || 0 })}
                      className="w-24"
                      data-testid={`session-option-price-${idx}`}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600"
                    onClick={() => removeOption(idx)}
                    disabled={options.length <= 1}
                    aria-label="Remove session option"
                    title={options.length <= 1 ? 'Keep at least one option' : 'Remove this option'}
                    data-testid={`session-option-remove-${idx}`}
                  >
                    <Trash2 size={15} />
                  </Button>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={addOption}
              data-testid="session-option-add"
            >
              <Plus size={14} className="mr-1" /> Add another option
            </Button>
          </div>

          <div>
            <Label>Availability note (optional)</Label>
            <Input
              value={value.availability_note || ''}
              onChange={(e) => onPatch({ availability_note: e.target.value })}
              className="mt-1"
              placeholder={DEFAULT_AVAILABILITY_NOTE}
              data-testid="availability-note-input"
            />
            <p className="text-xs text-stone-500 mt-1">
              Shown on the class card in place of a schedule.
            </p>
          </div>

          <p className="text-xs text-stone-600 bg-blue-50 border border-blue-200 rounded p-2.5">
            Day, date, time and class-size fields are hidden for private lessons. Students pay for a session
            online and tell you their preferred days &amp; times — you then confirm the appointment with them.
          </p>
        </div>
      )}
    </div>
  );
};
